import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { parseCookies } from 'nookies';
import { useState } from 'react';

import { useBestOffersModal } from '@/hooks/useBestOffersModal';
import { Meta } from '@/layouts/Meta';
import { SavingsAccountsLayout } from '@/layouts/SavingsAccountsLayout';
import { MapDepositsFromServer } from '@/services/supabase';
import { prepareSearchQueryToPayload } from '@/store/storeSearch.utils';
import { Main } from '@/templates/Main';
import type { IBankDepositCard, SavingsAccountsProps } from '@/types';
import { calculateHighestRate } from '@/utils/calculateHighestRate';
import { getCurrentMonthYear } from '@/utils/dates';
import { makeServerProps } from '@/utils/nextjs';
import { useSeoText } from '@/utils/useSeoText';

import ApiSingleton from '../../../services/ApiSingleton';

export const getServerSideProps = makeServerProps()
  .withAction(async (context, _, userCountry) => {
    const payload = prepareSearchQueryToPayload(
      context?.query,
      context?.locale,
    );
    const cookies = parseCookies({ req: context?.req });
    const apiSingleton = ApiSingleton.getInstance();

    let isTestUser = cookies.test_user === 'true';

    if (context.res.req.headers.test_user === 'true') {
      isTestUser = true;
    }
    if (context.res.req.headers.test_user === 'false') {
      isTestUser = false;
    }

    const {
      data: initialPartnerSavingsAccounts,
      meta: partnerSavingsAccountsMeta,
    } = await apiSingleton.getDepositsListRequest(context.locale || 'en', {
      is_test: isTestUser,
      page: 1,
      product_types: ['savings account', 'demand account'],
      is_partner: true,
      countries: [userCountry.alpha2],
      currencies:
        userCountry.currency === 'all' ? undefined : [userCountry.currency],
      cashback: payload.cashback ? payload.cashback : undefined,
      annual_fee: payload['annual-fee'] ? payload['annual-fee'] : undefined,
    });

    const initialPartnerSavingsAccountsCards: IBankDepositCard[] =
      initialPartnerSavingsAccounts?.map((deposit: any) =>
        MapDepositsFromServer(deposit),
      )!;

    const { data: initialSavingsAccounts, meta: savingsAccountsMeta } =
      await apiSingleton.getDepositsListRequest(context.locale || 'en', {
        is_test: isTestUser,
        page: 1,
        product_types: ['savings account', 'demand account'],
        is_partner: false,
        countries: [userCountry.alpha2],
        currencies:
          userCountry.currency === 'all' ? undefined : [userCountry.currency],
        cashback: payload.cashback ? payload.cashback : undefined,
        annual_fee: payload['annual-fee'] ? payload['annual-fee'] : undefined,
      });

    const initialSavingsAccountsCards: IBankDepositCard[] =
      initialSavingsAccounts?.map((deposit: any) =>
        MapDepositsFromServer(deposit),
      )!;

    return {
      props: {
        initialSavingsAccounts: initialSavingsAccountsCards,
        initialPartnerSavingsAccounts: initialPartnerSavingsAccountsCards,
        savingsAccountsMeta,
        partnerSavingsAccountsMeta,
        country: userCountry,
        ...(await serverSideTranslations(context.locale || 'en', ['common'])),
      },
    };
  })
  .build();

const Deposits: NextPage<SavingsAccountsProps> = ({
  initialSavingsAccounts,
  initialPartnerSavingsAccounts,
  savingsAccountsMeta,
  partnerSavingsAccountsMeta,
  country,
}) => {
  const { t } = useTranslation();
  const { getSavingsAccountsSections } = useSeoText();

  const monthYear = getCurrentMonthYear();
  const [highestRate] = useState(
    calculateHighestRate(
      initialSavingsAccounts[0]?.grossInterestRate,
      initialPartnerSavingsAccounts[0]?.grossInterestRate,
    ),
  );

  useBestOffersModal();

  return (
    <Main
      meta={
        <Meta
          title={t('seo.savings-accounts.main.title', {
            date: monthYear,
          })}
          description={t('seo.savings-accounts.main.description')}
        />
      }
      shouldHeaderBeBlue
    >
      <SavingsAccountsLayout
        initialPartnerSavingsAccounts={initialPartnerSavingsAccounts}
        initialSavingsAccounts={initialSavingsAccounts}
        initialPartnerSavingsAccountsMeta={partnerSavingsAccountsMeta}
        initialSavingsAccountsMeta={savingsAccountsMeta}
        sections={getSavingsAccountsSections({
          country: country.name,
          monthYear,
          maxRate: highestRate || 0,
        })}
      />
    </Main>
  );
};

export default Deposits;
